/* ==========================================================================
   IAMONEAI - Application Constants
   Single source of truth for all app-wide constants
   ========================================================================== */

// ==========================================================================
// CONTEXTS (Memory Modes)
// ==========================================================================

export const CONTEXTS = {
  PERSONAL: 'personal',
  WORK: 'work',
  FAMILY: 'family',
};

export const CONTEXT_CONFIG = {
  [CONTEXTS.PERSONAL]: {
    id: 'personal',
    name: 'Personal',
    icon: '🏠',
    color: 'var(--context-personal)',
    bgColor: 'var(--context-personal-bg)',
    borderColor: 'var(--context-personal-border)',
    tone: 'friendly',
    style: 'casual',
    description: 'Your personal life, hobbies, and interests',
  },
  [CONTEXTS.WORK]: {
    id: 'work',
    name: 'Work',
    icon: '💼',
    color: 'var(--context-work)',
    bgColor: 'var(--context-work-bg)',
    borderColor: 'var(--context-work-border)',
    tone: 'professional',
    style: 'formal',
    description: 'Your career, projects, and professional goals',
  },
  [CONTEXTS.FAMILY]: {
    id: 'family',
    name: 'Family',
    icon: '👨‍👩‍👧‍👦',
    color: 'var(--context-family)',
    bgColor: 'var(--context-family-bg)',
    borderColor: 'var(--context-family-border)',
    tone: 'warm',
    style: 'caring',
    description: 'Your family relationships and responsibilities',
  },
};

export const DEFAULT_CONTEXT = CONTEXTS.PERSONAL;


// ==========================================================================
// MEMORY TYPES
// ==========================================================================

export const MEMORY_TYPES = {
  IDENTITY: 'identity',
  LONG_TERM: 'long_term',
  SHORT_TERM: 'short_term',
  EPISODIC: 'episodic',
  PREFERENCE: 'preference',
  SKILL: 'skill',
  BELIEF: 'belief',
  GOAL: 'goal',
  TASK: 'task',
  EMOTION: 'emotion',
  SYSTEM: 'system',
};

export const MEMORY_TYPE_LABELS = {
  [MEMORY_TYPES.IDENTITY]: 'Identity',
  [MEMORY_TYPES.LONG_TERM]: 'Long-term',
  [MEMORY_TYPES.SHORT_TERM]: 'Short-term',
  [MEMORY_TYPES.EPISODIC]: 'Episode',
  [MEMORY_TYPES.PREFERENCE]: 'Preference',
  [MEMORY_TYPES.SKILL]: 'Skill',
  [MEMORY_TYPES.BELIEF]: 'Belief',
  [MEMORY_TYPES.GOAL]: 'Goal',
  [MEMORY_TYPES.TASK]: 'Task',
  [MEMORY_TYPES.EMOTION]: 'Emotion',
  [MEMORY_TYPES.SYSTEM]: 'System',
};


// ==========================================================================
// EPISTEMIC TYPES
// ==========================================================================

export const EPISTEMIC_TYPES = {
  FACT: 'fact',
  OPINION: 'opinion',
  BELIEF: 'belief',
};

export const EPISTEMIC_CONFIG = {
  [EPISTEMIC_TYPES.FACT]: {
    id: 'fact',
    label: 'Fact',
    icon: '✓',
    color: 'var(--epistemic-fact)',
    bgColor: 'var(--epistemic-fact-bg)',
    description: 'Verifiable, objective information',
  },
  [EPISTEMIC_TYPES.OPINION]: {
    id: 'opinion',
    label: 'Opinion',
    icon: '💭',
    color: 'var(--epistemic-opinion)',
    bgColor: 'var(--epistemic-opinion-bg)',
    description: 'Subjective preference or viewpoint',
  },
  [EPISTEMIC_TYPES.BELIEF]: {
    id: 'belief',
    label: 'Belief',
    icon: '💫',
    color: 'var(--epistemic-belief)',
    bgColor: 'var(--epistemic-belief-bg)',
    description: 'Internal belief about self or world',
  },
};


// ==========================================================================
// GOAL STATUS & CATEGORIES
// ==========================================================================

export const GOAL_STATUS = {
  PENDING: 'pending',
  DONE: 'done',
  PAUSED: 'paused',
  ABANDONED: 'abandoned',
  OVERDUE: 'overdue',
};

export const GOAL_STATUS_CONFIG = {
  [GOAL_STATUS.PENDING]: {
    id: 'pending',
    label: 'In Progress',
    icon: '⏳',
    color: 'var(--color-warning)',
  },
  [GOAL_STATUS.DONE]: {
    id: 'done',
    label: 'Completed',
    icon: '✅',
    color: 'var(--color-success)',
  },
  [GOAL_STATUS.PAUSED]: {
    id: 'paused',
    label: 'Paused',
    icon: '⏸️',
    color: 'var(--text-tertiary)',
  },
  [GOAL_STATUS.ABANDONED]: {
    id: 'abandoned',
    label: 'Abandoned',
    icon: '❌',
    color: 'var(--color-error)',
  },
  [GOAL_STATUS.OVERDUE]: {
    id: 'overdue',
    label: 'Overdue',
    icon: '⚠️',
    color: 'var(--color-error)',
  },
};

export const GOAL_CATEGORIES = {
  CAREER: 'career',
  FINANCE: 'finance',
  HEALTH: 'health',
  EDUCATION: 'education',
  RELATIONSHIP: 'relationship',
  FAMILY: 'family',
  TRAVEL: 'travel',
  HOME: 'home',
  GIFTS: 'gifts',
  PERSONAL: 'personal',
};

export const GOAL_CATEGORY_CONFIG = {
  [GOAL_CATEGORIES.CAREER]: { label: 'Career', icon: '💼', color: '#3b82f6' },
  [GOAL_CATEGORIES.FINANCE]: { label: 'Finance', icon: '💰', color: '#22c55e' },
  [GOAL_CATEGORIES.HEALTH]: { label: 'Health', icon: '💪', color: '#ef4444' },
  [GOAL_CATEGORIES.EDUCATION]: { label: 'Education', icon: '📚', color: '#8b5cf6' },
  [GOAL_CATEGORIES.RELATIONSHIP]: { label: 'Relationship', icon: '❤️', color: '#ec4899' },
  [GOAL_CATEGORIES.FAMILY]: { label: 'Family', icon: '👨‍👩‍👧‍👦', color: '#f59e0b' },
  [GOAL_CATEGORIES.TRAVEL]: { label: 'Travel', icon: '✈️', color: '#06b6d4' },
  [GOAL_CATEGORIES.HOME]: { label: 'Home', icon: '🏠', color: '#84cc16' },
  [GOAL_CATEGORIES.GIFTS]: { label: 'Gifts', icon: '🎁', color: '#f43f5e' },
  [GOAL_CATEGORIES.PERSONAL]: { label: 'Personal', icon: '🎯', color: '#6366f1' },
};


// ==========================================================================
// PERSONALITY SETTINGS (5-Axis Model)
// ==========================================================================

export const PERSONALITY_AXES = {
  ENERGY: 'energy',
  FORMALITY: 'formality',
  HUMOR: 'humor',
  DIRECTNESS: 'directness',
  EMPATHY: 'empathy',
};

export const PERSONALITY_CONFIG = {
  [PERSONALITY_AXES.ENERGY]: {
    id: 'energy',
    label: 'Energy',
    lowLabel: 'Calm',
    highLabel: 'Energetic',
    lowIcon: '😌',
    highIcon: '⚡',
    description: 'How enthusiastic and energetic responses should be',
    default: 0.5,
  },
  [PERSONALITY_AXES.FORMALITY]: {
    id: 'formality',
    label: 'Formality',
    lowLabel: 'Casual',
    highLabel: 'Formal',
    lowIcon: '👋',
    highIcon: '🎩',
    description: 'How professional and formal the tone should be',
    default: 0.3,
  },
  [PERSONALITY_AXES.HUMOR]: {
    id: 'humor',
    label: 'Humor',
    lowLabel: 'Serious',
    highLabel: 'Playful',
    lowIcon: '📋',
    highIcon: '😄',
    description: 'How much humor and playfulness to include',
    default: 0.4,
  },
  [PERSONALITY_AXES.DIRECTNESS]: {
    id: 'directness',
    label: 'Directness',
    lowLabel: 'Gentle',
    highLabel: 'Direct',
    lowIcon: '🌸',
    highIcon: '🎯',
    description: 'How straightforward and to-the-point responses should be',
    default: 0.5,
  },
  [PERSONALITY_AXES.EMPATHY]: {
    id: 'empathy',
    label: 'Empathy',
    lowLabel: 'Neutral',
    highLabel: 'Warm',
    lowIcon: '😐',
    highIcon: '🤗',
    description: 'How emotionally supportive and caring to be',
    default: 0.7,
  },
};


// ==========================================================================
// COMMUNICATION SETTINGS
// ==========================================================================

export const RESPONSE_LENGTH = {
  BRIEF: 'brief',
  CONCISE: 'concise',
  BALANCED: 'balanced',
  DETAILED: 'detailed',
};

export const RESPONSE_LENGTH_CONFIG = {
  [RESPONSE_LENGTH.BRIEF]: {
    id: 'brief',
    label: 'Brief',
    description: '1-2 sentences',
    icon: '📝',
  },
  [RESPONSE_LENGTH.CONCISE]: {
    id: 'concise',
    label: 'Concise',
    description: '2-3 sentences',
    icon: '📄',
  },
  [RESPONSE_LENGTH.BALANCED]: {
    id: 'balanced',
    label: 'Balanced',
    description: '3-5 sentences',
    icon: '📑',
  },
  [RESPONSE_LENGTH.DETAILED]: {
    id: 'detailed',
    label: 'Detailed',
    description: 'Comprehensive explanations',
    icon: '📚',
  },
};

export const EMOJI_USAGE = {
  NONE: 'none',
  MINIMAL: 'minimal',
  MODERATE: 'moderate',
  FREQUENT: 'frequent',
};

export const EMOJI_USAGE_CONFIG = {
  [EMOJI_USAGE.NONE]: { id: 'none', label: 'None', description: 'No emojis' },
  [EMOJI_USAGE.MINIMAL]: { id: 'minimal', label: 'Minimal', description: 'Occasional' },
  [EMOJI_USAGE.MODERATE]: { id: 'moderate', label: 'Moderate', description: 'Regular use' },
  [EMOJI_USAGE.FREQUENT]: { id: 'frequent', label: 'Frequent', description: 'Lots of emojis' },
};

export const QUESTION_STYLE = {
  CURIOUS: 'curious',
  DIRECT: 'direct',
  SUPPORTIVE: 'supportive',
  MINIMAL: 'minimal',
};

export const QUESTION_STYLE_CONFIG = {
  [QUESTION_STYLE.CURIOUS]: { id: 'curious', label: 'Curious', description: 'Exploratory follow-ups' },
  [QUESTION_STYLE.DIRECT]: { id: 'direct', label: 'Direct', description: 'Straight to the point' },
  [QUESTION_STYLE.SUPPORTIVE]: { id: 'supportive', label: 'Supportive', description: 'Encouraging questions' },
  [QUESTION_STYLE.MINIMAL]: { id: 'minimal', label: 'Minimal', description: 'Fewer questions' },
};


// ==========================================================================
// GUARDRAILS & DOMAINS
// ==========================================================================

export const DOMAINS = {
  CHAT: 'chat',
  META: 'meta',
  PRODUCTIVITY: 'productivity',
  LEARNING: 'learning',
  MEMORY: 'memory',
  KNOWLEDGE: 'knowledge',
  IDENTITY: 'identity',
  FINANCE: 'finance',
  MEDICAL: 'medical',
  LEGAL: 'legal',
};

export const DOMAIN_CONFIG = {
  [DOMAINS.CHAT]: { label: 'General Chat', icon: '💬', sensitive: false },
  [DOMAINS.META]: { label: 'Self-Awareness', icon: '🤖', sensitive: false },
  [DOMAINS.PRODUCTIVITY]: { label: 'Productivity', icon: '📋', sensitive: false },
  [DOMAINS.LEARNING]: { label: 'Learning', icon: '📚', sensitive: false },
  [DOMAINS.MEMORY]: { label: 'Memory', icon: '🧠', sensitive: false },
  [DOMAINS.KNOWLEDGE]: { label: 'Knowledge', icon: '💡', sensitive: false },
  [DOMAINS.IDENTITY]: { label: 'Identity', icon: '🪪', sensitive: true },
  [DOMAINS.FINANCE]: { label: 'Finance', icon: '💰', sensitive: true },
  [DOMAINS.MEDICAL]: { label: 'Medical', icon: '🏥', sensitive: true },
  [DOMAINS.LEGAL]: { label: 'Legal', icon: '⚖️', sensitive: true },
};

export const DEFAULT_ALLOWED_DOMAINS = [
  DOMAINS.CHAT,
  DOMAINS.META,
  DOMAINS.PRODUCTIVITY,
  DOMAINS.LEARNING,
  DOMAINS.MEMORY,
  DOMAINS.KNOWLEDGE,
  DOMAINS.IDENTITY,
];

export const DEFAULT_RESTRICTED_DOMAINS = [
  DOMAINS.FINANCE,
  DOMAINS.MEDICAL,
];


// ==========================================================================
// MEMORY MONITORING
// ==========================================================================

export const MEMORY_MONITORING = {
  FULL: 'full',
  SMART: 'smart',
  MANUAL: 'manual',
  OFF: 'off',
};

export const MEMORY_MONITORING_CONFIG = {
  [MEMORY_MONITORING.FULL]: {
    id: 'full',
    label: 'Full',
    description: 'Remember everything from conversations',
    icon: '🧠',
  },
  [MEMORY_MONITORING.SMART]: {
    id: 'smart',
    label: 'Smart',
    description: 'Remember important things automatically',
    icon: '✨',
  },
  [MEMORY_MONITORING.MANUAL]: {
    id: 'manual',
    label: 'Manual',
    description: 'Only remember when asked',
    icon: '👆',
  },
  [MEMORY_MONITORING.OFF]: {
    id: 'off',
    label: 'Off',
    description: 'Don\'t save new memories',
    icon: '🚫',
  },
};


// ==========================================================================
// LLM PROVIDERS
// ==========================================================================

export const LLM_PROVIDERS = {
  OPENAI: 'openai',
  ANTHROPIC: 'anthropic',
  GOOGLE: 'google',
  USER_BYOK: 'user_byok',
};

export const LLM_PROVIDER_CONFIG = {
  [LLM_PROVIDERS.OPENAI]: {
    id: 'openai',
    name: 'OpenAI',
    models: ['gpt-4o', 'gpt-4o-mini'],
    icon: '🟢',
    active: true,
  },
  [LLM_PROVIDERS.ANTHROPIC]: {
    id: 'anthropic',
    name: 'Anthropic',
    models: ['claude-3-opus', 'claude-3-sonnet'],
    icon: '🟠',
    active: false,
  },
  [LLM_PROVIDERS.GOOGLE]: {
    id: 'google',
    name: 'Google',
    models: ['gemini-pro'],
    icon: '🔵',
    active: false,
  },
  [LLM_PROVIDERS.USER_BYOK]: {
    id: 'user_byok',
    name: 'Your API Key',
    models: [],
    icon: '🔑',
    active: false,
  },
};


// ==========================================================================
// AUTHENTICATION
// ==========================================================================

export const AUTH_CONFIG = {
  CODE_LENGTH: 6,
  CODE_EXPIRY_MINUTES: 10,
  MAX_ATTEMPTS: 5,
  LOCKOUT_MINUTES: 15,
  RESEND_COOLDOWN_SECONDS: 60,
  SESSION_DURATION_DAYS: 30,
};


// ==========================================================================
// LOCAL STORAGE KEYS
// ==========================================================================

export const STORAGE_KEYS = {
  TOKEN: 'iamoneai_token',
  IIN: 'iamoneai_iin',
  CONTEXT: 'iamoneai_context',
  THEME: 'iamoneai_theme',
  ONBOARDING_COMPLETE: 'iamoneai_onboarding',
  LAST_ACTIVE: 'iamoneai_last_active',
};


// ==========================================================================
// API ENDPOINTS
// ==========================================================================

export const API_ENDPOINTS = {
  // Auth
  SEND_CODE: '/verify/send-code',
  VERIFY_CODE: '/verify/check',
  REGISTER: '/register',
  
  // Identity
  LOOKUP: '/lookup',
  
  // Brain
  BRAIN_GET: '/brain',
  BRAIN_UPDATE: '/brain/update',
  
  // Chat
  CHAT: '/ai/chat',
  
  // Memory
  MEMORY_GET: '/memory',
  MEMORY_ADD: '/memory',
  
  // Knowledge
  KNOWLEDGE_GET: '/knowledge',
  KNOWLEDGE_ADD: '/knowledge',
  
  // Analytics
  ANALYTICS: '/analytics',
};


// ==========================================================================
// UI LIMITS
// ==========================================================================

export const UI_LIMITS = {
  MAX_MESSAGE_LENGTH: 4000,
  MAX_MEMORY_DISPLAY: 50,
  MAX_GOALS_DISPLAY: 20,
  CHAT_HISTORY_LIMIT: 100,
  TYPING_DEBOUNCE_MS: 300,
  TOAST_DURATION_MS: 5000,
  MODAL_CLOSE_DELAY_MS: 200,
};


// ==========================================================================
// MESSAGES
// ==========================================================================

export const MESSAGES = {
  WELCOME: "Hey! I'm your personal AI guardian. What's on your mind?",
  WELCOME_ANONYMOUS: "Hi! I'm IAMONEAI. I can chat with you, but I won't remember our conversation. Create an account to unlock permanent memory.",
  LOADING: "Thinking...",
  ERROR_GENERIC: "Something went wrong. Please try again.",
  ERROR_NETWORK: "Network error. Please check your connection.",
  ERROR_UNAUTHORIZED: "Session expired. Please log in again.",
  GUARDRAIL_BLOCKED: "I respect your boundaries. You've set this as a restricted domain.",
  MEMORY_SAVED: "Got it! I'll remember that.",
  GOAL_CREATED: "Goal created! I'll help you track it.",
  CONTEXT_SWITCHED: "Switched to {context} mode.",
};


// ==========================================================================
// TWO LAWS (Core Philosophy)
// ==========================================================================

export const TWO_LAWS = {
  LAW_ONE: {
    title: 'You Are The Owner',
    description: 'You decide what IAMONEAI can and cannot do. You set the boundaries, not the AI. Your values are never overridden.',
  },
  LAW_TWO: {
    title: 'I Protect You',
    description: 'I block actions YOU marked as restricted. I warn you about potential harm. I guard your identity from manipulation. I enforce YOUR rules, not what I think is best.',
  },
};


// ==========================================================================
// EXPORT ALL
// ==========================================================================

export default {
  CONTEXTS,
  CONTEXT_CONFIG,
  DEFAULT_CONTEXT,
  MEMORY_TYPES,
  MEMORY_TYPE_LABELS,
  EPISTEMIC_TYPES,
  EPISTEMIC_CONFIG,
  GOAL_STATUS,
  GOAL_STATUS_CONFIG,
  GOAL_CATEGORIES,
  GOAL_CATEGORY_CONFIG,
  PERSONALITY_AXES,
  PERSONALITY_CONFIG,
  RESPONSE_LENGTH,
  RESPONSE_LENGTH_CONFIG,
  EMOJI_USAGE,
  EMOJI_USAGE_CONFIG,
  QUESTION_STYLE,
  QUESTION_STYLE_CONFIG,
  DOMAINS,
  DOMAIN_CONFIG,
  DEFAULT_ALLOWED_DOMAINS,
  DEFAULT_RESTRICTED_DOMAINS,
  MEMORY_MONITORING,
  MEMORY_MONITORING_CONFIG,
  LLM_PROVIDERS,
  LLM_PROVIDER_CONFIG,
  AUTH_CONFIG,
  STORAGE_KEYS,
  API_ENDPOINTS,
  UI_LIMITS,
  MESSAGES,
  TWO_LAWS,
};